export type DropdownItem<T> = {
    label: string;
    value: T;
};

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    forwardRef,
    HostBinding,
    HostListener,
    inject
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';

@Component({
    selector: 'app-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DropdownComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DropdownComponent<T> implements ControlValueAccessor {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    @ViewChild(NgSelectComponent)
    public readonly ngSelectComponent?: NgSelectComponent;

    @Input() public items: DropdownItem<T>[];
    @Input() public disabled = false;
    @Input() public loading: boolean;
    @Input() public placeholder: string;
    @Input() public ngSelectClass = 'single';
    @Input() public searchable = false;
    @Input() public highlightAnimated: boolean = false;
    @Input() public highlightFocused: boolean = false;
    @Output() public selectedItem = new EventEmitter<T>();
    @Output() public touched = new EventEmitter();
    @Output() public highlightFocus = new EventEmitter<void>();

    @HostBinding('attr.tabindex') public hostTabIndex = '0';
    @HostBinding('attr.role') public hostRole = 'combobox';
    @HostBinding('attr.aria-haspopup') public ariaHasPopup = 'listbox';
    @HostBinding('attr.aria-expanded') public get ariaExpanded() {
        return this.open ? 'true' : 'false';
    }

    public open = false;

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public onTouched = () => {};
    public onChange = (value: T): T => value;
    public value: T;

    public registerOnChange(fn: (value: T) => T) {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void) {
        this.onTouched = fn;
    }

    public setDisabledState(disabled: boolean) {
        this.disabled = disabled;
        this.changeDetectorRef.markForCheck();
    }

    public writeValue(value: T) {
        if (!this.disabled) {
            this.value = value;
            this.changeDetectorRef.markForCheck();
        }
    }

    public selectionChanged(value: T) {
        this.selectedItem.emit(value);
        this.onChange(value);
    }

    public focus() {
        this.onTouched();
        this.touched.emit();
        this.highlightFocus.emit();
        this.ngSelectComponent?.focus();
    }

    public onHighlightFocus() {
        this.highlightFocus.emit();
    }

    public close() {
        this.open = false;
        this.ngSelectComponent?.close();
        this.changeDetectorRef.markForCheck();
    }

    public openDropdown() {
        if (this.disabled) {
            return;
        }
        this.open = true;
        this.ngSelectComponent?.open();
        this.ngSelectComponent?.focus();
        this.changeDetectorRef.markForCheck();
    }

    public toggle() {
        if (this.disabled) {
            return;
        }
        if (this.open) {
            this.close();
        } else {
            this.openDropdown();
        }
    }

    // Host listeners for click + keyboard
    @HostListener('click', ['$event'])
    public onHostClick(event: Event) {
        event.stopPropagation();
        this.focus();
        //this.toggle();
    }

    @HostListener('keydown', ['$event'])
    public onHostKeydown(event: KeyboardEvent) {
        if (this.disabled) {
            return;
        }

        const key = event.key;
        if (key === 'Enter' || key === ' ' || key === 'ArrowDown') {
            event.preventDefault();
            this.openDropdown();
        } else if (key === 'Escape') {
            event.preventDefault();
            this.close();
        } else if (key === 'ArrowUp') {
            event.preventDefault();
            this.openDropdown();
        }
    }

    public applyFilter(ref: NgSelectComponent, $event: Event) {
        const term = ($event.target as HTMLInputElement).value;
        ref.filter(term);
    }
}
